import type ApolloCache from '@wora/apollo-cache'
import type { TaskQuery, SearchLooseyTasksQuery, SearchTemplateTasksQuery, Task } from '../generated/types'
import {
  TaskDocument,
  SearchLooseyTasksDocument,
  SearchTemplateTasksDocument,
  FullTaskFragmentDoc
} from '../generated/types'
import { defaultDataIdFromObject } from 'apollo-cache-inmemory'
import type { CacheProxy } from '.'
import { updateQueries } from './util'

/**
 * Updates any cached tasks in the apollo cache
 */
export function updateCachedTask(proxy: CacheProxy, task: Task) {
  const cache = proxy as ApolloCache
  cache.writeQuery({
    query: TaskDocument,
    variables: { id: task._id },
    data: {
      task: {
        __typename: 'Task',
        ...task
      }
    }
  })

  // update all searchLooseyTasks queries to remove the task
  if (task._deleted) {
    updateQueries<SearchLooseyTasksQuery>(cache, {
      query: SearchLooseyTasksDocument,
      update: (data) => {
        const filteredTasks = data.searchTasks.tasks.filter((q) => q._id !== task._id)

        return {
          ...data,
          searchTasks: {
            ...data.searchTasks,
            tasks: filteredTasks,
            total_rows:
              filteredTasks.length !== data.searchTasks.tasks.length
                ? data.searchTasks.total_rows - 1
                : data.searchTasks.total_rows
          }
        }
      }
    })

    updateQueries<SearchTemplateTasksQuery>(cache, {
      query: SearchTemplateTasksDocument,
      update: (data) => {
        const filteredTasks = data.searchTasks.tasks.filter((q) => q._id !== task._id)

        return {
          ...data,
          searchTasks: {
            ...data.searchTasks,
            tasks: filteredTasks,
            total_rows:
              filteredTasks.length !== data.searchTasks.tasks.length
                ? data.searchTasks.total_rows - 1
                : data.searchTasks.total_rows
          }
        }
      }
    })
  } else {
    const sortTasks = (tasks: (typeof task)[]) =>
      tasks.sort((a, b) => {
        if (!a.startDateTime) return 1
        if (!b.startDateTime) return -1
        return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
      })

    const isTemplateTask = !!(task.template as any)?.id

    if (!isTemplateTask) {
      // update all searchLooseyTasks queries to add the task
      updateQueries<SearchLooseyTasksQuery>(cache, {
        query: SearchLooseyTasksDocument,
        update: (data, variables) => {
          const existingTask = data.searchTasks.tasks.find((q) => q._id === task._id)
          if (existingTask) {
            return data // No change needed
          }

          // Only add to queries whose dealId filter matches this task
          const queryStr = variables?.query || ''
          if (
            task.dealId &&
            queryStr.includes('dealId:') &&
            !queryStr.includes(`dealId:"${task.dealId}"`) &&
            !queryStr.includes(`dealId:${task.dealId}`)
          ) {
            return data
          }

          return {
            ...data,
            searchTasks: {
              ...data.searchTasks,
              tasks: sortTasks([...data.searchTasks.tasks, task]),
              total_rows: data.searchTasks.total_rows + 1
            }
          }
        }
      })
    } else {
      // update all searchTemplateTasks queries to add the task
      updateQueries<SearchTemplateTasksQuery>(cache, {
        query: SearchTemplateTasksDocument,
        update: (data, variables) => {
          const existingTask = data.searchTasks.tasks.find((q) => q._id === task._id)
          if (existingTask) {
            return data // No change needed
          }

          // Only add to queries whose dealId filter matches this task
          const queryStr = variables?.query || ''
          if (
            task.dealId &&
            queryStr.includes('dealId:') &&
            !queryStr.includes(`dealId:"${task.dealId}"`) &&
            !queryStr.includes(`dealId:${task.dealId}`)
          ) {
            return data
          }

          return {
            ...data,
            searchTasks: {
              ...data.searchTasks,
              tasks: sortTasks([...data.searchTasks.tasks, task]),
              total_rows: data.searchTasks.total_rows + 1
            }
          }
        }
      })
    }
  }

  cache.writeFragment({
    fragment: FullTaskFragmentDoc,
    id: defaultDataIdFromObject({ id: task._id, __typename: 'Task' }),
    data: {
      __typename: 'Task',
      ...task
    }
  })
}

export function updateCachedTaskPartially(cache: ApolloCache, dealId: string, values: Partial<Task>) {
  try {
    const task = cache.readQuery<TaskQuery>({
      query: TaskDocument,
      variables: { id: dealId }
    }).task

    updateCachedTask(cache, {
      ...task,
      ...values
    })
  } catch (_e) {
    console.warn('Unable to find task for id ' + dealId)
  }
}
